import { Fragment, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { EmptyState } from './EmptyState'
import { ErrorState } from './ErrorState'
import { SkeletonRows } from './Skeleton'
import { useIsDesktop } from './useMediaQuery'

export interface Column<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  align?: 'left' | 'right' | 'center'
  /** A CSS width for the column: "96px", "20%". */
  width?: string
  /** How the column shows in card mode (below 768px): `title` heads the card,
   *  `meta` sits beside the title (a status pill), `line` is a label/value
   *  line (the default), `sub` is a plain line under the title with no
   *  label (a date, a channel), `actions` is the card's button row at the
   *  bottom, `hidden` is left out. */
  card?: 'title' | 'meta' | 'sub' | 'line' | 'actions' | 'hidden'
  /** Card mode only: leave this line out for a row where it has nothing to
   *  say ("요청 —" on a campaign post). */
  cardHide?: (row: T) => boolean
  /** Only in card mode: a line that sums several columns up for the phone
   *  ("US · 최우선 · ▲ 4") and has no column of its own in the table. */
  cardOnly?: boolean
  /** Table mode: shown from 1280px up only — a column the narrower table can
   *  do without (경쟁사's 개발사 at 1024px). */
  wideOnly?: boolean
  className?: string
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T) => string | number
  /** Names the table for screen readers. */
  label: string
  onRowClick?: (row: T) => void
  rowClassName?: (row: T) => string | undefined
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  /** What an empty table shows — an EmptyState with the next step. */
  empty?: ReactNode
  /** `auto` switches to cards under 768px. */
  mode?: 'auto' | 'table' | 'cards'
  /** A row that opens in place (변동's top-10 app list): the detail is drawn
   *  under the row, full width, or at the foot of its card. The toggle itself
   *  is a column the page supplies; clicks inside the detail do not reach
   *  `onRowClick`. */
  expand?: { isOpen: (row: T) => boolean; render: (row: T) => ReactNode }
  className?: string
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' }

function activate<T>(e: KeyboardEvent, row: T, onRowClick?: (row: T) => void) {
  if (!onRowClick) return
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    onRowClick(row)
  }
}

/** Rows of things. Below 768px each row becomes a card, so no table is ever
 *  wider than the phone. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  label,
  onRowClick,
  rowClassName,
  loading,
  error,
  onRetry,
  empty,
  mode = 'auto',
  expand,
  className,
}: DataTableProps<T>) {
  const desktop = useIsDesktop()
  if (error) return <ErrorState title={`${label}을(를) 불러오지 못했다`} error={error} onRetry={onRetry} />
  if (loading && !rows) return <SkeletonRows className={className} />
  if (!rows || rows.length === 0) {
    return <>{empty ?? <EmptyState title="아직 없다" />}</>
  }
  const cards = mode === 'cards' || (mode === 'auto' && !desktop)
  const tableColumns = columns.filter((c) => !c.cardOnly)
  return cards ? (
    <Cards columns={columns} rows={rows} rowKey={rowKey} label={label} onRowClick={onRowClick} rowClassName={rowClassName} expand={expand} className={className} />
  ) : (
    <div className={cn('overflow-x-auto rounded-lg border border-border bg-card', className)}>
      <table className="w-full border-collapse text-table" aria-label={label}>
        <thead>
          <tr className="border-b border-border">
            {tableColumns.map((c) => (
              <th
                key={c.key}
                scope="col"
                style={c.width ? { width: c.width } : undefined}
                className={cn(
                  'h-9 whitespace-nowrap px-3 text-meta font-medium text-muted-foreground',
                  ALIGN[c.align ?? 'left'],
                  c.wideOnly && 'hidden xl:table-cell',
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={rowKey(r)}>
              <tr
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={(e) => activate(e, r, onRowClick)}
                tabIndex={onRowClick ? 0 : undefined}
                className={cn(
                  'border-b border-border last:border-b-0',
                  onRowClick && 'cursor-pointer hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none',
                  rowClassName?.(r),
                )}
              >
                {tableColumns.map((c) => (
                  <td
                    key={c.key}
                    className={cn('h-row px-3 py-1.5 align-middle', ALIGN[c.align ?? 'left'], c.wideOnly && 'hidden xl:table-cell', c.className)}
                  >
                    {c.cell(r)}
                  </td>
                ))}
              </tr>
              {expand?.isOpen(r) && (
                <tr className="border-b border-border bg-muted/30 last:border-b-0">
                  <td colSpan={tableColumns.length} className="px-3 py-3">
                    {expand.render(r)}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Cards<T>({
  columns,
  rows,
  rowKey,
  label,
  onRowClick,
  rowClassName,
  expand,
  className,
}: Pick<DataTableProps<T>, 'columns' | 'rowKey' | 'label' | 'onRowClick' | 'rowClassName' | 'expand' | 'className'> & { rows: T[] }) {
  const title = columns.find((c) => c.card === 'title') ?? columns[0]
  const meta = columns.filter((c) => c.card === 'meta')
  const subs = columns.filter((c) => c.card === 'sub')
  const lines = columns.filter((c) => c !== title && (c.card ?? 'line') === 'line')
  const actions = columns.filter((c) => c.card === 'actions')
  return (
    <ul className={cn('space-y-2', className)} aria-label={label}>
      {rows.map((r) => (
        <li
          key={rowKey(r)}
          onClick={onRowClick ? () => onRowClick(r) : undefined}
          onKeyDown={(e) => activate(e, r, onRowClick)}
          tabIndex={onRowClick ? 0 : undefined}
          className={cn(
            'rounded-lg border border-border bg-card p-3',
            onRowClick && 'cursor-pointer active:bg-muted/60',
            rowClassName?.(r),
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 text-body font-medium">{title.cell(r)}</div>
            {meta.length > 0 && <div className="flex shrink-0 items-center gap-1.5">{meta.map((c) => <span key={c.key}>{c.cell(r)}</span>)}</div>}
          </div>
          {subs.map((c) => (
            <div key={c.key} className="mt-0.5 min-w-0 text-meta text-muted-foreground">
              {c.cell(r)}
            </div>
          ))}
          {lines.length > 0 && (
            // One label width for every card, so the values line up down a
            // list instead of starting wherever each card's longest label ends.
            <dl className="mt-2 grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-table">
              {lines.filter((c) => !c.cardHide?.(r)).map((c) => (
                <div key={c.key} className="contents">
                  <dt className="break-words text-muted-foreground">{c.header}</dt>
                  <dd className="min-w-0 break-words">{c.cell(r)}</dd>
                </div>
              ))}
            </dl>
          )}
          {actions.map((c) => (
            // No rule above it: a ruled footer for one or two icons made every
            // card look like a form.
            <div key={c.key} className="mt-2 flex justify-end [&>*]:justify-end">
              {c.cell(r)}
            </div>
          ))}
          {expand?.isOpen(r) && (
            <div className="mt-3 cursor-default border-t border-border pt-3" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {expand.render(r)}
            </div>
          )}
        </li>
      ))}
    </ul>
  )
}
